/**
 * Scrips API client — fetch wrapper with the full auth header set.
 *
 * Auth flow:
 *   1. Pre-flight: auth-store.getValidAccessToken() refreshes if exp < 30s.
 *   2. Send the request with Authorization + org/practice/user/role context.
 *   3. On 401: auth-store.forceRefresh() (singleton-promise guarded), retry once.
 *   4. Refresh failure inside auth-store triggers logout(); the retry's 401
 *      propagates as ApiError so callers see the failure.
 */

import { useAuthStore } from '@/auth/auth-store';
import { getClientIpAddress } from './ip-address';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(`API Error ${status}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function buildHeaders(
  accessToken: string,
  ipAddress: string,
  extras?: HeadersInit,
  isMultipart = false,
): Headers {
  const { user } = useAuthStore.getState();
  const headers = new Headers(extras);

  // For multipart bodies the browser sets Content-Type with the boundary
  // automatically — never set it ourselves or the server can't parse it.
  if (!isMultipart && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('AppType', 'Practice Management');
  headers.set('userTimeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);

  // IpAddress — load-bearing for any request that triggers the backend's
  // gRPC sub-call path. `AppointmentManagementService.GetGrpcMetaData()`
  // reads this header and passes it as gRPC metadata; if absent, the
  // gRPC call throws ArgumentNullException AFTER the DB write completes,
  // so the HTTP response 500s even though the mutation succeeded.
  // Mirrors Flutter `dio_api_client.dart:367`. See `ip-address.ts` for
  // the lookup. Always present (falls back to "0.0.0.0" on lookup fail).
  headers.set('IpAddress', ipAddress);

  // Org/practice context — strictly from the authenticated user. NEVER fall
  // back to DEV_CONFIG: leaking the dev tenant's ids on a real user's request
  // crosses tenant boundaries and is a HIPAA-grade bug (PROD-773). If the
  // user object is missing these, omit the header and let the backend reject
  // with a clear error rather than silently hitting the wrong tenant.
  if (user?.organizationId) headers.set('OrganizationId', user.organizationId);
  if (user?.practiceId) headers.set('PracticeId', user.practiceId);
  if (user?.userId) headers.set('UserId', user.userId);
  if (user?.role) headers.set('RoleId', user.role);

  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) throw new ApiError(res.status, await res.text());
  const text = await res.text();
  if (!text) return undefined as T;
  // Some endpoints return a bare value (e.g. a saved document's GUID as plain
  // text) rather than a JSON document. That's a successful response, so fall
  // back to the raw string instead of throwing a JSON.parse SyntaxError — which
  // otherwise surfaces as a false error on an operation that actually saved
  // (e.g. practice-stamp upload returning the new doc id).
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
}

export async function apiClient<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  return parseResponse<T>(await fetchWithAuth(endpoint, options));
}

/** The absolute URL of an API path (`/api/...`), for the few calls this client cannot send itself. */
export const apiUrl = (endpoint: string) => `${BASE_URL}${endpoint}`;

/**
 * One authenticated request, with the same headers and 401-retry as `apiClient`, that returns the raw `Response`
 * unread. For callers that need the status or a response header (Document Intake reads the run `ETag`), or send
 * `If-Match` / `Idempotency-Key` in `init.headers`.
 */
export async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = apiUrl(endpoint);
  const isMultipart = options.body instanceof FormData;

  // Resolve IP once per request — getClientIpAddress() is cached after
  // first lookup, so this is effectively free on the warm path. Don't
  // block the auth-token resolve on it though; both are independent.
  const [token, ipAddress] = await Promise.all([
    getValidAccessToken(),
    getClientIpAddress(),
  ]);

  const send = (currentToken: string) =>
    fetch(url, {
      ...options,
      headers: buildHeaders(currentToken, ipAddress, options.headers, isMultipart),
    });

  let res = await send(token);

  if (res.status === 401) {
    const refreshed = await forceRefresh();
    res = await send(refreshed);
  }

  return res;
}

/**
 * The full header set for a request this client cannot send itself: an `XMLHttpRequest` upload, which `fetch` cannot
 * report progress for (Document Intake batch upload). Pass `refresh: true` after a 401 to get a refreshed token.
 */
export async function authHeaders(opts: { multipart?: boolean; refresh?: boolean } = {}): Promise<Headers> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const [token, ipAddress] = await Promise.all([
    opts.refresh ? forceRefresh() : getValidAccessToken(),
    getClientIpAddress(),
  ]);
  return buildHeaders(token, ipAddress, undefined, opts.multipart ?? false);
}

/**
 * Per-call options accepted by every verb helper.
 * `signal` lets React Query (or any caller) cancel the in-flight fetch
 * when the consumer unmounts or the query key changes.
 */
export interface ApiCallOptions {
  signal?: AbortSignal;
}

export const get = <T>(endpoint: string, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, { method: 'GET', signal: opts.signal });

export const post = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    signal: opts.signal,
  });

export const put = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    signal: opts.signal,
  });

export const patch = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
    signal: opts.signal,
  });

export const del = <T>(endpoint: string, body?: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  });

/** Multipart POST. Pass a FormData body — the browser sets Content-Type. */
export const postForm = <T>(endpoint: string, body: FormData, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, { method: 'POST', body, signal: opts.signal });

/**
 * POST that returns the response body as raw text — used by endpoints that
 * return a bare string (e.g. Reporting `/ReportDownload` returns a URL).
 * Same auth + refresh-on-401 machinery as `apiClient`, just no JSON.parse.
 */
export async function postText(
  endpoint: string,
  body: unknown,
  opts: ApiCallOptions = {},
): Promise<string> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${BASE_URL}${endpoint}`;

  const [token, ipAddress] = await Promise.all([
    getValidAccessToken(),
    getClientIpAddress(),
  ]);

  const send = (currentToken: string) =>
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: buildHeaders(currentToken, ipAddress, undefined, false),
      signal: opts.signal,
    });

  let res = await send(token);

  if (res.status === 401) {
    const refreshed = await forceRefresh();
    res = await send(refreshed);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.text();
}

/**
 * Multipart POST that returns the response body as raw text — used by
 * endpoints that accept a `FormData` body AND return a bare string
 * (e.g. UploadPatientDocument returns the signed blob URL as plain
 * text). Same auth + refresh-on-401 machinery as `apiClient`, just no
 * JSON.parse and the body is FormData so the browser sets the
 * multipart boundary automatically.
 *
 * Error handling parity with `postText` (Samer review PR #137 §1):
 *   When `res.ok` is false, the FULL response body is captured into
 *   `ApiError.body`. The global MutationCache `onError` handler
 *   (`src/app/providers/query-client.ts` → `showServerError` →
 *   `parseValidationProblem`) then parses the body as a .NET
 *   ValidationProblemDetails envelope and surfaces the field-level
 *   error messages to the user. No silent generic-failure swallow.
 */
export async function postFormText(
  endpoint: string,
  body: FormData,
  opts: ApiCallOptions = {},
): Promise<string> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${BASE_URL}${endpoint}`;

  const [token, ipAddress] = await Promise.all([
    getValidAccessToken(),
    getClientIpAddress(),
  ]);

  const send = (currentToken: string) =>
    fetch(url, {
      method: 'POST',
      body,
      headers: buildHeaders(currentToken, ipAddress, undefined, true),
      signal: opts.signal,
    });

  let res = await send(token);

  if (res.status === 401) {
    const refreshed = await forceRefresh();
    res = await send(refreshed);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.text();
}

/**
 * GET that returns the response body as a Blob — used for file downloads
 * (CSV templates, exports). Goes through the same auth + refresh-on-401
 * machinery as `apiClient`.
 */
export async function getBlob(
  endpoint: string,
  opts: ApiCallOptions = {},
): Promise<Blob> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${BASE_URL}${endpoint}`;

  const [token, ipAddress] = await Promise.all([
    getValidAccessToken(),
    getClientIpAddress(),
  ]);

  const send = (currentToken: string) =>
    fetch(url, {
      headers: buildHeaders(currentToken, ipAddress, undefined, false),
      signal: opts.signal,
    });

  let res = await send(token);

  if (res.status === 401) {
    const refreshed = await forceRefresh();
    res = await send(refreshed);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.blob();
}
